/*
License

Copyright (c) 2022 Chukwubuikem Ume-Ugwa

Permission is hereby witheld, any person obtaining a copy
of this software and associated documentation files (the "Software"), cannot
use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software. These restrictions can be only lifted with
a written permission from Chukwubuikem Ume-Ugwa.
*/
import {
  collection,
  query,
  onSnapshot,
  deleteDoc,
  doc,
  where,
  setDoc,
  updateDoc,
  getDoc,
  increment,
  limit,
  getDocs,
  orderBy,
} from 'firebase/firestore';

import { same as sameFn, diff as diffFn } from 'fast-array-diff';
import Schemas, { Constraints } from './schemas';
import { db, storage } from '../index';
import {
  ref,
  deleteObject,
  getBlob,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';
import { storageAdapter } from './cache';
import { log } from '../utils';

const YEARLY = 3.154e10;
const MONTHLY = 2.592e9;

const {
  queue_schema,
  db_node_schema,
  track_schema,
  storage_schema,
  stats_schema,
} = Schemas;

const [MT] = Constraints.schedule;
const { PUBLIC } = Constraints.queueType;
function compareTracks(t0, t1) {
  const verdict =
    t0[track_schema.track_address] === t1[track_schema.track_address];
  if (verdict) {
    if (t0[track_schema.track_uri].startsWith('blob')) {
      t0[track_schema.title] = t1[track_schema.title];
      t0[track_schema.artist] = t1[track_schema.artist];
      t0[track_schema.price] = t1[track_schema.price];
      t0[track_schema.pricing_unit] = t1[track_schema.pricing_unit];
    } else {
      t1[track_schema.title] = t0[track_schema.title];
      t1[track_schema.artist] = t0[track_schema.artist];
      t1[track_schema.price] = t0[track_schema.price];
      t1[track_schema.pricing_unit] = t0[track_schema.pricing_unit];
    }
  }
  return verdict;
}

class FirebaseFacade {
  #cache = {};
  #queueRefreshTime = 8.64e7;
  #trackRefreshTime = 3.6e6;

  constructor() {
    setInterval(() => {
      const queues = Object.keys(this.#cache);
      for (let i = 0; i < queues.length; i++) {
        this.loadTracks(tracks => {
          this.#cache[queues[i]] = tracks;
        });
      }
    }, this.#queueRefreshTime);

    setInterval(() => {
      const queues = Object.keys(this.#cache);
      for (let i = 0; i < queues.length; i++) {
        if (this.#cache[queues[i]] !== undefined) {
          this.#cache[queues[i]].forEach(track => {
            URL.revokeObjectURL(track.track_uri);
            track.reload();
          });
        }
      }
    }, this.#trackRefreshTime);
  }

  loadPublicQueues(callback) {
    const { PUBLIC } = Constraints.queueType;
    const queueQuery = query(
      collection(db, db_node_schema.queues),
      where(queue_schema.type, '==', PUBLIC)
    );
    return onSnapshot(queueQuery, async snapshot => {
      const queues = await snapshot.docs.map(queue => queue.data());
      log({
        component: 'FirebaseFacade',
        msg: 'Received new public queues',
        queuew: queues,
      });
      callback(queues);
    });
  }

  loadPrivateQueues(callback, owner_wallet_address) {
    const queueQuery = query(
      collection(db, db_node_schema.queues),
      where(queue_schema.owner_wallet_address, '==', owner_wallet_address)
    );
    return onSnapshot(queueQuery, async snapshot => {
      const queues = await snapshot.docs.map(queue => queue.data());
      log({
        component: 'FirebaseFacade',
        msg: 'Received new private queues',
        queuew: queues,
      });
      callback(queues);
    });
  }

  loadGeoFencedQueues(callback) {
    const queueQuery = query(
      collection(db, db_node_schema.queues),
      where(queue_schema.type, '==', Constraints.queueType.GEOFENCED)
    );
    return onSnapshot(queueQuery, async snapshot => {
      const queues = await snapshot.docs.map(queue => queue.data());
      log({
        component: 'FirebaseFacade',
        msg: 'Received new Geofenced queues',
        queuew: queues,
      });
      callback(queues);
    });
  }

  async deleteQueue(queue_address) {
    const queueRef = doc(db, db_node_schema.queues, queue_address);
    await deleteDoc(queueRef);

    try {
      await deleteObject(
        ref(storage, `${storage_schema.artworks}/${queue_address}`)
      );
    } catch {}

    const deletedQueueRef = doc(
      db,
      db_node_schema.deleted_queues,
      queue_address
    );
    await setDoc(deletedQueueRef, { deleted: true });
  }

  async loadAudio(track) {
    const uri = track.track_uri;
    if (uri.startsWith('blob')) return;
    log({
      component: 'FirebaseFacade',
      msg: 'loading track',
      uri: uri,
      track: track,
    });

    try {
      const trackRef = ref(storage, track.track_uri);
      const audio = await getBlob(trackRef);
      track.track_uri = URL.createObjectURL(audio);
      track.reload = async function () {
        log({
          component: 'FirebaseFacade',
          msg: 'Reloading track',
          uri: uri,
          track: track,
        });
        try {
          const audio = await getBlob(trackRef);
          track.track_uri = URL.createObjectURL(audio);
        } catch (error) {
          log({
            component: 'FirebaseFacade$track',
            msg: 'error trying to reload audio file',
            error: error,
          });
        }
      };
    } catch (error) {
      log({
        component: 'FirebaseFacade',
        msg: 'error trying to load audio file',
        error: error,
      });
    }

    return track;
  }

  loadTracks(callback, { queue_address }) {
    if (queue_address) {
      if (this.#cache[queue_address] !== undefined) {
        callback(this.#cache[queue_address]);
      } else {
        const tracksQuery = query(
          collection(
            db,
            db_node_schema.queues,
            queue_address,
            db_node_schema.tracks
          ),
          orderBy(track_schema.created_on)
        );

        this.#innerAsync(callback, tracksQuery, queue_address);
      }
    }
  }

  async #innerAsync(callback, tracksQuery, queue_address) {
    const snapshot = await getDocs(tracksQuery);
    this.#processTrackSnapshot(snapshot, callback, queue_address);
  }

  async #processTrackSnapshot(snapshot, callback, queue_address) {
    const currentTracks = this.#cache[queue_address] || [];
    const tracks = await snapshot.docs.map(track => track.data());

    const same = sameFn(currentTracks, tracks, compareTracks);
    const { added, removed } = diffFn(currentTracks, tracks, compareTracks);
    const updatedTracks = same
      .concat(added)
      .sort((t0, t1) => t0.created_on - t1.created_on);

    setTimeout(() => callback(updatedTracks), 100);
    this.#cache[queue_address] = updatedTracks;

    this.updateQueueData(updatedTracks, queue_address);
    log({
      component: 'FirebaseFacade',
      added: added,
      same: same,
      removed: removed,
    });
  }

  listenForTrackChanges(callback, { queue_address }) {
    if (queue_address) {
      const tracksQuery = query(
        collection(
          db,
          db_node_schema.queues,
          queue_address,
          db_node_schema.tracks
        ),
        orderBy(track_schema.created_on)
      );

      const unregister = onSnapshot(tracksQuery, snapshot => {
        this.#processTrackSnapshot(snapshot, callback, queue_address, true);
      });
      function unsubscribe() {
        log({
          component: 'FirebaseFacade',
          msg: 'unregistered listener',
          queue_address: queue_address,
        });
        unregister();
      }

      log({
        component: 'FirebaseFacade',
        msg: 'registered listener',
        queue_address: queue_address,
      });
      return unsubscribe;
    }
  }

  listenForQueueChanges(callback, { queue_address }) {
    if (queue_address) {
      const tracksQuery = query(doc(db, db_node_schema.queues, queue_address));

      const unregister = onSnapshot(tracksQuery, snapshot => {
        if (snapshot.exists()) {
          callback(snapshot.data());
        }
      });
      function unsubscribe() {
        log({
          component: 'FirebaseFacade',
          msg: 'unregistered listener',
          queue_address: queue_address,
        });
        unregister();
      }

      log({
        component: 'FirebaseFacade',
        msg: 'registered listener',
        queue_address: queue_address,
      });
      return unsubscribe;
    }
  }

  loadQueue(callback, queue_address) {
    if (queue_address) {
      const queueRef = doc(db, db_node_schema.queues, queue_address);
      getDoc(queueRef)
        .then(queue => callback(queue.data()))
        .catch(__ => log('error', __));
    }
  }

  async updateQueueData(tracks, queue_address) {
    const queueRef = doc(db, db_node_schema.queues, queue_address);

    let totalSeconds = tracks.reduce((prev, curr) => prev + curr.duration, 0);
    return updateDoc(queueRef, {
      number_of_tracks: tracks.length,
      number_of_hours: Math.floor((totalSeconds / 3600) * 100) / 100,
    });
  }

  async deleteTrack(track) {
    const artworkRef = ref(
      storage,
      `${storage_schema.artworks}/${track.track_address}`
    );
    const mp3Ref = ref(
      storage,
      `${storage_schema.tracks}/${track.track_address}`
    );

    return deleteObject(artworkRef)
      .then(__ => deleteObject(mp3Ref))
      .then(__ =>
        deleteDoc(
          doc(
            db,
            db_node_schema.queues,
            track.queue_address,
            db_node_schema.tracks,
            track.track_address
          )
        )
      )
      .catch(({ code }) => {
        if (code === 'storage/object-not-found') {
          return deleteObject(mp3Ref)
            .then(() =>
              deleteDoc(
                doc(
                  db,
                  db_node_schema.queues,
                  track.queue_address,
                  db_node_schema.tracks,
                  track.track_address
                )
              )
            )
            .catch(error => console.error(error));
        }
      });
  }

  async isSubscriber(publicKey, queue) {
    try {
      const document = await getDoc(
        doc(
          db,
          db_node_schema.subscriptions,
          queue[queue_schema.queue_address],
          db_node_schema.subscribers,
          publicKey.toBase58()
        )
      );

      if (document.exists()) {
        const { expiration } = document.data();
        return Date.now() < expiration;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  }

  async addSubscriber(publicKey, queue, schedule) {
    if (publicKey !== null) {
      return setDoc(
        doc(
          db,
          db_node_schema.subscriptions,
          queue[queue_schema.queue_address],
          db_node_schema.subscribers,
          publicKey.toBase58()
        ),
        {
          expiration: Date.now() + (schedule === MT ? MONTHLY : YEARLY),
        }
      );
    }
  }

  async uploadArt(address, art, metadata) {
    const artRef = ref(storage, `${storage_schema.artworks}/${address}`);
    await uploadBytes(artRef, art, metadata);

    return getDownloadURL(artRef);
  }

  async updateDocument(docPath, document) {
    const docRef = doc(db, docPath);
    return updateDoc(docRef, document);
  }

  async insertDocument(docRef, document) {
    return setDoc(docRef, document);
  }

  async createTrackCounter(queueRef) {
    const batch = db.batch();
    const { shards } = Constraints;

    for (let idx = 0; idx < shards; idx++) {
      const shardRef = queueRef
        .collection(db_node_schema.shards)
        .doc(idx.toString());
      batch.set(shardRef, { count: 0 });
    }

    return batch.commit();
  }

  async incrementTrackCounter(queueRef, count) {
    const { shards } = Constraints;
    const shard_id = Math.floor(Math.random() * shards).toString();

    const shard_ref = queueRef.collection(db_node_schema.shards).doc(shard_id);
    return shard_ref.update(db_node_schema.count, increment(count));
  }

  createQueueRef() {
    return doc(collection(db, db_node_schema.queues));
  }

  getFileRef(path) {
    return ref(storage, `${path}`);
  }

  async uploadFile(fileRef, file) {
    try {
      const uploadResult = await uploadBytes(fileRef, file);
      return Promise.resolve({ ...uploadResult, error: null });
    } catch (error) {
      log({ msg: 'Failed to upload file', error: error });
      Promise.reject({ error: error });
    }
  }

  createTrackRef(queue) {
    return doc(
      collection(
        db,
        `${db_node_schema.queues}/${queue[queue_schema.queue_address]}/${
          db_node_schema.tracks
        }`
      )
    );
  }

  async createDownloadUrl(storageRef) {
    return getDownloadURL(storageRef);
  }

  async loadStats(queue) {
    const address = queue[queue_schema.queue_address];
    const stats = { grossing: 0, streams: 0, subscribers: 0 };

    try {
      const remoteStats = await getDoc(
        doc(
          db,
          `${db_node_schema.stats}/${
            db_node_schema.monthly
          }/${this.getDate()}/${address}`
        )
      );

      const data = remoteStats.data();
      if (data !== undefined) {
        return data;
      }
    } catch (error) {}

    return stats;
  }

  async updateCollabs(queue) {
    const address = queue[queue_schema.queue_address];
    const docRef = doc(db, `${db_node_schema.queues}/${address}`);

    try {
      await updateDoc(docRef, { collabs: increment(1) });
    } catch (error) {
      try {
        this.insertDocument(docRef, {
          queue_address: address,
          collabs: increment(1),
        });
      } catch (error) {}
    }
  }

  async updateMonthlyStreams(queue) {
    const address = queue[queue_schema.queue_address];
    const docRef = doc(
      db,
      `${db_node_schema.stats}/${
        db_node_schema.monthly
      }/${this.getDate()}/${address}`
    );

    try {
      if (queue[queue_schema.number_of_tracks] > 0) {
        await updateDoc(docRef, { streams: increment(1) });
      }
    } catch (error) {
      try {
        this.insertDocument(docRef, {
          queue_address: address,
          streams: increment(1),
        });
      } catch (error) {}
    }
  }

  async updateMonthlySubscribers(queue) {
    const address = queue[queue_schema.queue_address];
    const docRef = doc(
      db,
      `${db_node_schema.stats}/${
        db_node_schema.monthly
      }/${this.getDate()}/${address}`
    );

    try {
      await updateDoc(docRef, { subscribers: increment(1) });
    } catch (error) {
      try {
        this.insertDocument(docRef, {
          queue_address: address,
          subscribers: increment(1),
        });
      } catch (error) {}
    }
  }

  async updateMonthlyGrossing(queue, amount) {
    const address = queue[queue_schema.queue_address];
    const docRef = doc(
      db,
      `${db_node_schema.stats}/${
        db_node_schema.monthly
      }/${this.getDate()}/${address}`
    );

    try {
      await updateDoc(docRef, { grossing: increment(amount) });
    } catch (error) {
      try {
        this.insertDocument(docRef, {
          queue_address: address,
          grossing: increment(amount),
        });
      } catch (error) {}
    }
  }

  async updateMonthlyTrackCount(queue, count) {
    const address = queue[queue_schema.queue_address];
    const docRef = doc(
      db,
      `${db_node_schema.stats}/${
        db_node_schema.monthly
      }/${this.getDate()}/${address}`
    );

    try {
      await updateDoc(docRef, { number_of_tracks: increment(count) });
    } catch (error) {
      try {
        this.insertDocument(docRef, {
          queue_address: address,
          number_of_tracks: increment(count),
        });
      } catch (error) {}
    }
  }

  async updateMonthlyTrackHours(queue, hours) {
    const address = queue[queue_schema.queue_address];
    const docRef = doc(
      db,
      `${db_node_schema.stats}/${
        db_node_schema.monthly
      }/${this.getDate()}/${address}`
    );

    try {
      await updateDoc(docRef, { number_of_hours: increment(hours) });
    } catch (error) {
      try {
        this.insertDocument(docRef, {
          queue_address: address,
          number_of_hours: increment(hours),
        });
      } catch (error) {}
    }
  }

  async updateMonthlyCollabs(queue) {
    const address = queue[queue_schema.queue_address];
    const docRef = doc(
      db,
      `${db_node_schema.stats}/${
        db_node_schema.monthly
      }/${this.getDate()}/${address}`
    );

    try {
      await updateDoc(docRef, { collabs: increment(1) });
    } catch (error) {
      try {
        this.insertDocument(docRef, {
          queue_address: address,
          collabs: increment(1),
        });
      } catch (error) {}
    }
  }

  getDate() {
    const date = new Date();
    return `${date.getUTCFullYear()}-${date.getUTCMonth() + 1}`;
  }

  async getMonthlyTrendingQueue(count = 10, threshold = 10) {
    try {
      const collectionPath = `${db_node_schema.stats}/${
        db_node_schema.monthly
      }/${this.getDate()}`;

      const queuesQuery = query(
        collection(db, collectionPath),
        where(stats_schema.streams, '>', threshold),
        orderBy(stats_schema.streams, 'desc'),
        limit(count)
      );

      const querySnapshot = await getDocs(queuesQuery);
      const queues = [];
      for (let idx = 0; idx < querySnapshot.docs.length; idx++) {
        const statSnap = querySnapshot.docs[idx];
        if (statSnap.exists()) {
          const stat = statSnap.data();
          const snapshot = await getDoc(
            doc(
              db,
              `${db_node_schema.queues}/${stat[queue_schema.queue_address]}`
            )
          );
          if (snapshot.exists()) {
            const queue = snapshot.data();
            if (queue[queue_schema.type] === PUBLIC)
              queues.push({ queue: queue, stat: stat });
          }
        }
      }

      return queues;
    } catch (error) {
      log({ msg: 'Error extracting trending', error: error });
      return Promise.resolve([]);
    }
  }

  async getMonthlyTopGrossingQueue(count = 10, threshold = 10) {
    try {
      const collectionPath = `${db_node_schema.stats}/${
        db_node_schema.monthly
      }/${this.getDate()}`;

      const queuesQuery = query(
        collection(db, collectionPath),
        where(stats_schema.grossing, '>', threshold),
        orderBy(stats_schema.grossing, 'desc'),
        limit(count)
      );

      const querySnapshot = await getDocs(queuesQuery);
      const queues = [];
      for (let idx = 0; idx < querySnapshot.docs.length; idx++) {
        const statSnap = querySnapshot.docs[idx];
        if (statSnap.exists()) {
          const stat = statSnap.data();
          const snapshot = await getDoc(
            doc(
              db,
              `${db_node_schema.queues}/${stat[queue_schema.queue_address]}`
            )
          );
          if (snapshot.exists()) {
            const queue = snapshot.data();
            if (queue[queue_schema.type] === PUBLIC)
              queues.push({ queue: queue, stat: stat });
          }
        }
      }

      return queues;
    } catch (error) {
      log({ msg: 'Error extracting top grossing', error: error });
      return Promise.resolve([]);
    }
  }

  async getMonthlyTopSubsQueue(count = 10, threshold = 10) {
    try {
      const collectionPath = `${db_node_schema.stats}/${
        db_node_schema.monthly
      }/${this.getDate()}`;

      const queuesQuery = query(
        collection(db, collectionPath),
        where(stats_schema.subscribers, '>', threshold),
        orderBy(stats_schema.subscribers, 'desc'),
        limit(count)
      );

      const querySnapshot = await getDocs(queuesQuery);
      const queues = [];
      for (let idx = 0; idx < querySnapshot.docs.length; idx++) {
        const statSnap = querySnapshot.docs[idx];
        if (statSnap.exists()) {
          const stat = statSnap.data();
          const snapshot = await getDoc(
            doc(
              db,
              `${db_node_schema.queues}/${stat[queue_schema.queue_address]}`
            )
          );
          if (snapshot.exists()) {
            const queue = snapshot.data();
            if (queue[queue_schema.type] === PUBLIC)
              queues.push({ queue: queue, stat: stat });
          }
        }
      }

      return queues;
    } catch (error) {
      log({ msg: 'Error extracting top subs', error: error });
      return Promise.resolve([]);
    }
  }

  async getMonthlyTopCollabQueue(count = 10, threshold = 10) {
    try {
      const collectionPath = `${db_node_schema.stats}/${
        db_node_schema.monthly
      }/${this.getDate()}`;

      const queuesQuery = query(
        collection(db, collectionPath),
        where(stats_schema.collabs, '>', threshold),
        orderBy(stats_schema.collabs, 'desc'),
        limit(count)
      );

      const querySnapshot = await getDocs(queuesQuery);
      const queues = [];
      for (let idx = 0; idx < querySnapshot.docs.length; idx++) {
        const statSnap = querySnapshot.docs[idx];
        if (statSnap.exists()) {
          const stat = statSnap.data();
          const snapshot = await getDoc(
            doc(
              db,
              `${db_node_schema.queues}/${stat[queue_schema.queue_address]}`
            )
          );
          if (snapshot.exists()) {
            const queue = snapshot.data();
            if (queue[queue_schema.type] === PUBLIC)
              queues.push({ queue: queue, stat: stat });
          }
        }
      }

      return queues;
    } catch (error) {
      log({ msg: 'Error extracting top collabs', error: error });
      return Promise.resolve([]);
    }
  }

  async loadDoc(docPath) {
    try {
      const document = await getDoc(doc(db, docPath));
      if (document.exists()) {
        const data = document.data();
        return data;
      }
    } catch (error) {
      log({
        component: 'FirebaseFacade',
        error: error,
        msg: 'Error occurred trying load doc',
        docPath: docPath,
      });
    }
    return null;
  }
}

export const firebaseFacade = new FirebaseFacade();
